import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  FileText,
  Download,
  Trash2,
  AlertTriangle,
  AlertCircle,
  Info,
  CheckCircle2,
  Calendar,
  Building2,
  Hash,
  IndianRupee,
  Loader2,
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  Copy,
  Lock,
  Clock,
  Save,
  BadgeAlert,
  TrendingUp,
  Tag,
  Lightbulb,
  Shield
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [result, setResult] = useState(location.state?.result || null);
  const [processingMode] = useState(location.state?.processingMode || "private");
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  // Payable form state — pre-filled from extracted data
  const [payableForm, setPayableForm] = useState({
    vendor_name: "", vendor_gstin: "", invoice_number: "",
    invoice_date: "", due_date: "", subtotal: 0, tax_amount: 0, total_amount: 0,
  });
  const [savingPayable, setSavingPayable] = useState(false);
  const [savedToPayables, setSavedToPayables] = useState(false);

  useEffect(() => {
    if (!result) {
      navigate("/upload");
    }
  }, [result, navigate]);

  // Initialize payable form once data is available
  useEffect(() => {
    if (result?.extracted_data) {
      const d = result.extracted_data;
      setPayableForm({
        vendor_name: d.vendor_name || "",
        vendor_gstin: d.vendor_gstin || "",
        invoice_number: d.invoice_number || "",
        invoice_date: d.invoice_date || "",
        due_date: d.due_date || "",
        subtotal: d.subtotal || 0,
        tax_amount: d.tax_amount || 0,
        total_amount: d.total_amount || 0,
      });
    }
  }, [result]);

  const handleSaveToPayables = async () => {
    const vendorName = (payableForm.vendor_name || "").trim();
    if (!vendorName) { toast.error("Vendor name is required. Please enter the vendor/supplier name."); return; }

    // Sanitize: remove excessive whitespace, trim special chars
    const cleanVendor = vendorName.replace(/\s+/g, " ").replace(/[^\w\s.&,-\/()\u0900-\u097F]/g, "");
    if (!cleanVendor) { toast.error("Invalid vendor name. Please enter a valid name."); return; }

    setSavingPayable(true);
    try {
      // Get user's default company
      const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const companyId = meRes.data.default_company_id;
      if (!companyId) { toast.error("No company set up. Go to Settings first."); setSavingPayable(false); return; }

      const payload = {
        company_id: companyId,
        invoice_date: payableForm.invoice_date || new Date().toISOString().split("T")[0],
        due_date: payableForm.due_date || "",
        customer_name: cleanVendor,
        customer_gst: (payableForm.vendor_gstin || "").trim().toUpperCase(),
        customer_address: "",
        customer_email: "",
        items: (result.extracted_data?.items || []).map(i => ({
          name: (i.name || "Item").trim(),
          description: "",
          hsn_code: (i.hsn_code || "").trim(),
          quantity: i.quantity || 1,
          unit_price: i.unit_price || i.total_price || 0,
          gst_percent: i.gst_percent || 0,
          gst_not_applicable: false,
        })),
        discount: 0,
        shipping: 0,
        invoice_type: "purchase",
        notes: `Imported from analyzed invoice ${payableForm.invoice_number || ""}`.trim(),
      };

      // If no items, create single item from totals
      if (!payload.items.length || payload.items.every(i => !i.unit_price)) {
        payload.items = [{
          name: `Purchase from ${cleanVendor}`,
          description: "",
          hsn_code: "",
          quantity: 1,
          unit_price: payableForm.subtotal || payableForm.total_amount || 0,
          gst_percent: payableForm.subtotal > 0 ? Math.round((payableForm.tax_amount / payableForm.subtotal) * 100) : 18,
          gst_not_applicable: false,
        }];
      }

      await axios.post(`${API}/builder/invoices/create`, payload, { withCredentials: true });

      // Auto-save vendor to customers master if not already there
      try {
        await axios.post(`${API}/customers/`, {
          name: cleanVendor,
          gstin: (payableForm.vendor_gstin || "").trim().toUpperCase(),
          type: "vendor",
        }, { withCredentials: true });
      } catch {} // ignore if duplicate

      setSavedToPayables(true);
      toast.success("Purchase invoice saved — view in All Invoices");
      setTimeout(() => navigate("/invoices?type=purchase"), 1000);
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to save";
      toast.error(typeof detail === "string" ? detail : "Failed to save invoice. Check vendor name and try again.");
    } finally {
      setSavingPayable(false);
    }
  };

  if (!result) return null;

  const { extracted_data: data, insights, confidence_score, id } = result;

  // Per-field confidence lookup from backend field_confidences[]
  const fieldConfMap = (data?.field_confidences || []).reduce((acc, fc) => {
    acc[fc.field] = fc;
    return acc;
  }, {});

  const ConfChip = ({ field }) => {
    const fc = fieldConfMap[field];
    if (!fc) return null;
    const c = fc.confidence || 0;
    const label = c >= 0.8 ? "High" : c >= 0.5 ? "Needs Review" : "Low";
    const cls = c >= 0.8
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : c >= 0.5
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-red-50 text-red-700 border-red-200";
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`ml-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls} cursor-help`}
              data-testid={`conf-chip-${field}`}
            >
              {label}
              {c < 0.8 && <BadgeAlert className="w-2.5 h-2.5" />}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[240px]">
            <p className="text-xs">
              OCR confidence: <span className="font-semibold">{Math.round(c * 100)}%</span>
            </p>
            {fc.reason && <p className="text-xs text-slate-500 mt-1">{fc.reason}</p>}
            {!fc.reason && c >= 0.8 && <p className="text-xs text-slate-500 mt-1">Looks good — verify anyway.</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Categorize insights
  const anomalies = insights?.filter(i => ['duplicate', 'anomaly', 'mismatch', 'tax_error'].includes(i.type)) || [];
  const warnings = insights?.filter(i => i.severity === 'warning' || i.severity === 'error') || [];
  const suggestions = insights?.filter(i => ['high_cost', 'missing', 'gstin'].includes(i.type)) || [];

  const getSeverityIcon = (severity, type) => {
    if (type === 'duplicate') return <Copy className="w-4 h-4" />;
    if (type === 'mismatch') return <AlertTriangle className="w-4 h-4" />;
    if (type === 'high_cost') return <TrendingUp className="w-4 h-4" />;
    
    switch (severity) {
      case "error":
        return <AlertCircle className="w-4 h-4" />;
      case "warning":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  const getInsightTag = (insight) => {
    const tags = {
      duplicate: { text: "Possible duplicate", color: "#EF4444", bg: "#FEE2E2" },
      anomaly: { text: "Price higher than usual", color: "#F59E0B", bg: "#FEF3C7" },
      mismatch: { text: "Mismatch detected", color: "#EF4444", bg: "#FEE2E2" },
      high_cost: { text: "High value item", color: "#6366F1", bg: "#E0E7FF" },
      missing: { text: "Missing field", color: "#9CA3AF", bg: "#F3F4F6" },
      gstin: { text: "GSTIN", color: insight.severity === "error" ? "#EF4444" : insight.severity === "warning" ? "#F59E0B" : "#10B981", bg: insight.severity === "error" ? "#FEE2E2" : insight.severity === "warning" ? "#FEF3C7" : "#D1FAE5" },
      tax_error: { text: "Tax calculation", color: "#EF4444", bg: "#FEE2E2" },
    };
    return tags[insight.type] || { text: insight.type, color: "#6B7280", bg: "#F3F4F6" };
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case "error":
        return "border-l-[#EF4444] bg-[#FEF2F2]";
      case "warning":
        return "border-l-[#F59E0B] bg-[#FFFBEB]";
      default:
        return "border-l-[#6366F1] bg-[#EEF2FF]";
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportType("pdf");
    
    try {
      const response = await axios.post(
        `${API}/invoice/export/pdf`,
        result,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-report-${id.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF report downloaded!");
    } catch (error) {
      console.error("PDF export error:", error);
      toast.error("Failed to export PDF");
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportType("excel");
    
    try {
      const response = await axios.post(
        `${API}/invoice/export/excel`,
        result,
        { responseType: "blob" }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-report-${id.slice(0, 8)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Excel report downloaded!");
    } catch (error) {
      console.error("Excel export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    
    try {
      if (processingMode !== "private") {
        await axios.delete(`${API}/invoice/${id}`);
      }
      
      setIsDeleted(true);
      toast.success("Your file has been deleted", {
        description: "All invoice data has been removed from our servers.",
        icon: <CheckCircle2 className="w-5 h-5 text-[#10B981]" />
      });
      
      setTimeout(() => {
        navigate("/upload");
      }, 2000);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete invoice");
      setIsDeleting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: data.currency || "INR",
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getModeInfo = () => {
    const modes = {
      private: { icon: <Lock className="w-4 h-4" />, text: "Private Mode", color: "#10B981", desc: "Data not stored" },
      temp: { icon: <Clock className="w-4 h-4" />, text: "Temp Mode", color: "#F59E0B", desc: "Auto-deletes in 24h" },
      save: { icon: <Save className="w-4 h-4" />, text: "Save Mode", color: "#6366F1", desc: "Stored permanently" }
    };
    return modes[processingMode] || modes.private;
  };

  const modeInfo = getModeInfo();

  if (isDeleted) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-[#10B981]" />
          </div>
          <h2 className="text-xl font-semibold text-[#111827] mb-2">Data Deleted</h2>
          <p className="text-[#6B7280]">Redirecting to upload page...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#F9FAFB] py-6 sm:py-8" data-testid="results-page">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <Link
                to="/upload"
                className="inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#111827] mb-2"
                data-testid="back-link"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Upload
              </Link>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#111827]">
                Review Extracted Details
              </h1>
              <p className="text-sm text-[#6B7280] mt-1">
                OCR-assisted extraction. Please verify every field before saving.
              </p>
            </div>
            
            {/* Export Actions */}
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap" data-testid="export-actions">
              <button
                onClick={handleExportPDF}
                disabled={isExporting}
                className="btn-secondary flex items-center gap-2 text-sm py-2"
                data-testid="export-pdf-btn"
              >
                {isExporting && exportType === "pdf" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                PDF
              </button>
              <button
                onClick={handleExportExcel}
                disabled={isExporting}
                className="btn-secondary flex items-center gap-2 text-sm py-2"
                data-testid="export-excel-btn"
              >
                {isExporting && exportType === "excel" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <FileSpreadsheet className="w-4 h-4" />
                )}
                Excel
              </button>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting}
                    className="p-2 rounded-xl text-[#EF4444] hover:bg-[#EF4444]/10 transition-colors border border-[#FEE2E2]"
                    data-testid="delete-btn"
                  >
                    {isDeleting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Trash2 className="w-5 h-5" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete all data now</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* OCR Accuracy notice — always visible before review */}
          <div
            className="mb-6 rounded-xl border border-amber-200 bg-amber-50/70 p-4 flex items-start gap-3"
            data-testid="ocr-notice-banner"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900 mb-0.5">OCR Extraction Notice</p>
              <p className="text-xs text-amber-800 leading-relaxed">
                Extracted values may be inaccurate — HSN codes read as amounts, GST mismatched, item names incomplete, dates misread. Please review carefully before confirming.
              </p>
            </div>
          </div>

          {/* Invoice Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-6" data-testid="summary-cards">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="card-surface p-4 sm:p-5 col-span-2 lg:col-span-1"
            >
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4 text-[#2563EB]" />
                <span className="text-xs text-[#6B7280] uppercase tracking-wider">Vendor</span>
              </div>
              <p className="text-sm sm:text-base font-semibold text-[#111827] truncate" data-testid="vendor-name">
                {data.vendor_name || "Not detected"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="card-surface p-4 sm:p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <IndianRupee className="w-4 h-4 text-[#10B981]" />
                <span className="text-xs text-[#6B7280] uppercase tracking-wider">Total</span>
              </div>
              <p className="text-sm sm:text-base font-semibold text-[#111827]" data-testid="total-amount">
                {formatCurrency(data.total_amount)}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card-surface p-4 sm:p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-[#7C3AED]" />
                <span className="text-xs text-[#6B7280] uppercase tracking-wider">Date</span>
              </div>
              <p className="text-sm sm:text-base font-semibold text-[#111827]" data-testid="invoice-date">
                {data.invoice_date || "Not detected"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="card-surface p-4 sm:p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <Hash className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-xs text-[#6B7280] uppercase tracking-wider">Invoice #</span>
              </div>
              <p className="text-sm sm:text-base font-semibold text-[#111827]" data-testid="invoice-number">
                {data.invoice_number || "Not detected"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="card-surface p-4 sm:p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                <span className="text-xs text-[#6B7280] uppercase tracking-wider">OCR Confidence</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                  confidence_score >= 80
                    ? "bg-emerald-100 text-emerald-700"
                    : confidence_score >= 50
                      ? "bg-amber-100 text-amber-700"
                      : "bg-red-100 text-red-700"
                }`} data-testid="confidence-label">
                  {confidence_score >= 80 ? "High" : confidence_score >= 50 ? "Needs Review" : "Low"}
                </span>
                <span className="text-sm font-semibold text-[#111827]" data-testid="confidence-score">{confidence_score}%</span>
              </div>
            </motion.div>
          </div>

          {/* GSTIN Summary */}
          {(data.vendor_gstin || data.buyer_gstin) && (
            <div className="flex flex-wrap gap-3 mb-6" data-testid="gstin-summary">
              {data.vendor_gstin && (
                <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-[#E5E7EB]">
                  <Shield className="w-4 h-4 text-[#2563EB]" />
                  <span className="text-xs text-[#6B7280]">Vendor GSTIN:</span>
                  <span className="text-sm font-mono font-medium text-[#111827]" data-testid="vendor-gstin">{data.vendor_gstin}</span>
                </div>
              )}
              {data.buyer_gstin && (
                <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-[#E5E7EB]">
                  <Shield className="w-4 h-4 text-[#10B981]" />
                  <span className="text-xs text-[#6B7280]">Buyer GSTIN:</span>
                  <span className="text-sm font-mono font-medium text-[#111827]" data-testid="buyer-gstin">{data.buyer_gstin}</span>
                </div>
              )}
              {(data.cgst || data.sgst || data.igst) && (
                <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-[#E5E7EB]">
                  <Tag className="w-4 h-4 text-[#F59E0B]" />
                  <span className="text-xs text-[#6B7280]">Tax:</span>
                  {data.cgst != null && <span className="text-xs">CGST: {formatCurrency(data.cgst)}</span>}
                  {data.sgst != null && <span className="text-xs">SGST: {formatCurrency(data.sgst)}</span>}
                  {data.igst != null && <span className="text-xs">IGST: {formatCurrency(data.igst)}</span>}
                </div>
              )}
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Anomalies & Insights */}
            <div className="lg:col-span-1 space-y-6">
              {/* Anomaly Detection - Key Feature */}
              {anomalies.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="card-surface p-5 border-2 border-[#EF4444]/20"
                  data-testid="anomaly-section"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-[#EF4444]/10 flex items-center justify-center">
                      <BadgeAlert className="w-5 h-5 text-[#EF4444]" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-[#111827]">
                        Anomalies Detected
                      </h2>
                      <p className="text-xs text-[#EF4444]">{anomalies.length} issue{anomalies.length > 1 ? 's' : ''} found</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    {anomalies.map((insight, i) => {
                      const tag = getInsightTag(insight);
                      return (
                        <div
                          key={i}
                          className={`p-3 rounded-lg border-l-4 ${getSeverityClass(insight.severity)}`}
                          data-testid={`anomaly-${i}`}
                        >
                          <div className="flex items-start gap-2">
                            <span style={{ color: tag.color }}>{getSeverityIcon(insight.severity, insight.type)}</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <span 
                                  className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded"
                                  style={{ backgroundColor: tag.bg, color: tag.color }}
                                >
                                  {tag.text}
                                </span>
                              </div>
                              <p className="text-sm text-[#111827]">{insight.message}</p>
                              {insight.details && (
                                <p className="text-xs text-[#6B7280] mt-1">{insight.details}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Insights Panel */}
              <div className="card-surface p-5" data-testid="insights-panel">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-5 h-5 text-[#6366F1]" />
                  <h2 className="text-base font-semibold text-[#111827]">
                    Insights & Suggestions
                  </h2>
                </div>

                {suggestions.length > 0 ? (
                  <div className="space-y-2">
                    {suggestions.map((insight, i) => {
                      const tag = getInsightTag(insight);
                      return (
                        <div
                          key={i}
                          className="p-3 rounded-lg bg-[#F9FAFB] border border-[#F3F4F6]"
                          data-testid={`suggestion-${i}`}
                        >
                          <div className="flex items-start gap-2">
                            <span style={{ color: tag.color }}>{getSeverityIcon(insight.severity, insight.type)}</span>
                            <div>
                              <p className="text-sm text-[#111827]">{insight.message}</p>
                              {insight.details && (
                                <p className="text-xs text-[#6B7280] mt-1">{insight.details}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : anomalies.length === 0 ? (
                  <div className="p-4 rounded-lg bg-[#ECFDF5] text-center">
                    <CheckCircle2 className="w-8 h-8 text-[#10B981] mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#065F46]">No issues detected!</p>
                    <p className="text-xs text-[#047857] mt-1">This invoice looks good.</p>
                  </div>
                ) : null}

                {/* Spend Breakdown */}
                {data.subtotal && data.tax_amount && (
                  <div className="mt-4 pt-4 border-t border-[#F3F4F6]">
                    <p className="text-xs text-[#6B7280] uppercase tracking-wider mb-2">Spend Breakdown</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#6B7280]">Subtotal</span>
                        <span className="text-[#111827]">{formatCurrency(data.subtotal)}</span>
                      </div>
                      {data.cgst != null && (
                        <div className="flex justify-between">
                          <span className="text-[#6B7280]">CGST</span>
                          <span className="text-[#111827]">{formatCurrency(data.cgst)}</span>
                        </div>
                      )}
                      {data.sgst != null && (
                        <div className="flex justify-between">
                          <span className="text-[#6B7280]">SGST</span>
                          <span className="text-[#111827]">{formatCurrency(data.sgst)}</span>
                        </div>
                      )}
                      {data.igst != null && (
                        <div className="flex justify-between">
                          <span className="text-[#6B7280]">IGST</span>
                          <span className="text-[#111827]">{formatCurrency(data.igst)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-[#6B7280]">Tax Total</span>
                        <span className="text-[#111827]">{formatCurrency(data.tax_amount)}</span>
                      </div>
                      <div className="flex justify-between font-semibold pt-1 border-t border-[#F3F4F6]">
                        <span className="text-[#111827]">Total</span>
                        <span className="text-[#2563EB]">{formatCurrency(data.total_amount)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Line Items Table */}
            <div className="lg:col-span-2">
              <div className="card-surface overflow-hidden" data-testid="items-table">
                <div className="p-4 sm:p-5 border-b border-[#F3F4F6]">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#2563EB]" />
                    <h2 className="text-base font-semibold text-[#111827]">
                      Line Items
                    </h2>
                    <span className="text-xs bg-[#F3F4F6] text-[#6B7280] px-2 py-0.5 rounded-full">
                      {data.items?.length || 0} items
                    </span>
                  </div>
                </div>

                {data.items && data.items.length > 0 ? (
                  <>
                    <div className="overflow-x-auto">
                      <Table className="invoice-table">
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[45%]">Item</TableHead>
                            <TableHead className="text-right">Qty</TableHead>
                            <TableHead className="text-right">Unit Price</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {data.items.map((item, i) => {
                            // Check if this item has an anomaly
                            const hasAnomaly = insights?.some(
                              ins => ins.message.toLowerCase().includes(item.name.toLowerCase().split(' ')[0])
                            );
                            return (
                              <TableRow key={i} data-testid={`item-row-${i}`} className={hasAnomaly ? "bg-[#FEF2F2]" : ""}>
                                <TableCell className="font-medium text-[#111827]">
                                  <div className="flex items-center gap-2">
                                    {item.name}
                                    {hasAnomaly && (
                                      <span className="text-[10px] font-bold text-[#EF4444] bg-[#FEE2E2] px-1.5 py-0.5 rounded">
                                        CHECK
                                      </span>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-right text-[#6B7280]">
                                  {item.quantity || "—"}
                                </TableCell>
                                <TableCell className="text-right text-[#6B7280]">
                                  {formatCurrency(item.unit_price)}
                                </TableCell>
                                <TableCell className="text-right font-medium text-[#111827]">
                                  {formatCurrency(item.total_price)}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Totals */}
                    <div className="border-t border-[#F3F4F6] p-4 bg-[#F9FAFB]">
                      <div className="space-y-2 max-w-xs ml-auto">
                        {data.subtotal && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#6B7280]">Subtotal</span>
                            <span className="text-[#111827]">{formatCurrency(data.subtotal)}</span>
                          </div>
                        )}
                        {data.tax_amount && (
                          <div className="flex justify-between text-sm">
                            <span className="text-[#6B7280]">Tax (GST)</span>
                            <span className="text-[#111827]">{formatCurrency(data.tax_amount)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-base font-semibold pt-2 border-t border-[#E5E7EB]">
                          <span className="text-[#111827]">Total</span>
                          <span className="text-[#2563EB]">{formatCurrency(data.total_amount)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-8 text-center">
                    <FileText className="w-10 h-10 text-[#9CA3AF] mx-auto mb-2" />
                    <p className="text-[#6B7280]">No line items detected</p>
                  </div>
                )}
              </div>

              {/* Additional Info */}
              {(data.due_date || data.vendor_address) && (
                <div className="mt-6 card-surface p-5">
                  <h3 className="text-sm font-semibold text-[#111827] mb-3">
                    Additional Details
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    {data.due_date && (
                      <div>
                        <span className="text-[#6B7280]">Due Date</span>
                        <p className="font-medium text-[#111827]">{data.due_date}</p>
                      </div>
                    )}
                    {data.vendor_address && (
                      <div className="sm:col-span-2">
                        <span className="text-[#6B7280]">Vendor Address</span>
                        <p className="font-medium text-[#111827]">{data.vendor_address}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Save to Payables Section */}
          <div className="mt-8 card-surface p-5" data-testid="save-to-payables-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[#111827] flex items-center gap-2">
                <FileText className="w-4 h-4 text-[#2563EB]" />
                Save as Purchase Invoice (Payable)
              </h3>
              {savedToPayables && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-[#D1FAE5] text-[#059669] font-medium" data-testid="saved-badge">Saved</span>
              )}
            </div>
            <p className="text-sm text-[#6B7280] mb-4">Review and correct extracted data, then save as a purchase invoice in your payables.</p>

            {!savedToPayables ? (
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="flex items-center text-xs font-medium text-[#6B7280] mb-1">Vendor Name * <ConfChip field="vendor_name" /></label>
                    <input className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm" value={payableForm.vendor_name} onChange={e => setPayableForm(p => ({...p, vendor_name: e.target.value}))} data-testid="payable-vendor-name" />
                  </div>
                  <div>
                    <label className="flex items-center text-xs font-medium text-[#6B7280] mb-1">Vendor GSTIN <ConfChip field="vendor_gstin" /></label>
                    <input className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm font-mono uppercase" value={payableForm.vendor_gstin} onChange={e => setPayableForm(p => ({...p, vendor_gstin: e.target.value.toUpperCase()}))} maxLength={15} data-testid="payable-vendor-gstin" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center text-xs font-medium text-[#6B7280] mb-1">Invoice Number <ConfChip field="invoice_number" /></label>
                    <input className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm" value={payableForm.invoice_number} onChange={e => setPayableForm(p => ({...p, invoice_number: e.target.value}))} data-testid="payable-inv-number" />
                  </div>
                  <div>
                    <label className="flex items-center text-xs font-medium text-[#6B7280] mb-1">Invoice Date <ConfChip field="invoice_date" /></label>
                    <input type="date" className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm" value={payableForm.invoice_date} onChange={e => setPayableForm(p => ({...p, invoice_date: e.target.value}))} data-testid="payable-inv-date" />
                  </div>
                  <div>
                    <label className="flex items-center text-xs font-medium text-[#6B7280] mb-1">Due Date <ConfChip field="due_date" /></label>
                    <input type="date" className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm" value={payableForm.due_date} onChange={e => setPayableForm(p => ({...p, due_date: e.target.value}))} data-testid="payable-due-date" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="flex items-center text-xs font-medium text-[#6B7280] mb-1">Subtotal (₹) <ConfChip field="subtotal" /></label>
                    <input type="number" className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm" value={payableForm.subtotal} onChange={e => setPayableForm(p => ({...p, subtotal: parseFloat(e.target.value) || 0}))} data-testid="payable-subtotal" />
                  </div>
                  <div>
                    <label className="flex items-center text-xs font-medium text-[#6B7280] mb-1">Tax Amount (₹) <ConfChip field="tax_amount" /></label>
                    <input type="number" className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm" value={payableForm.tax_amount} onChange={e => setPayableForm(p => ({...p, tax_amount: parseFloat(e.target.value) || 0}))} data-testid="payable-tax" />
                  </div>
                  <div>
                    <label className="flex items-center text-xs font-medium text-[#6B7280] mb-1">Total Amount (₹) <ConfChip field="total_amount" /></label>
                    <input type="number" className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm" value={payableForm.total_amount} onChange={e => setPayableForm(p => ({...p, total_amount: parseFloat(e.target.value) || 0}))} data-testid="payable-total" />
                  </div>
                </div>

                <button
                  onClick={handleSaveToPayables}
                  disabled={savingPayable || !(payableForm.vendor_name || "").trim()}
                  className="btn-primary flex items-center gap-2"
                  data-testid="save-payable-btn"
                >
                  {savingPayable ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Confirm &amp; Save Purchase Invoice
                </button>
              </div>
            ) : (
              <div className="p-4 bg-[#F0FDF4] rounded-xl border border-[#BBF7D0] text-sm text-[#166534]" data-testid="payable-saved-msg">
                Purchase invoice saved successfully. View it in your <Link to="/financial-reports" className="underline font-medium">Purchase Register</Link> or <Link to="/receivables" className="underline font-medium">Payables dashboard</Link>.
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/upload"
              className="btn-primary flex items-center gap-2"
              data-testid="analyze-another-btn"
            >
              <Upload className="w-5 h-5" />
              Upload Another Invoice
            </Link>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="btn-secondary flex items-center gap-2 text-[#EF4444] border-[#FEE2E2] hover:bg-[#FEF2F2]"
              data-testid="delete-now-btn"
            >
              {isDeleting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Trash2 className="w-5 h-5" />
              )}
              Delete Data Now
            </button>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
}
